import { useState } from "react";
import { useTranslation } from "../../i18n/LanguageContext";

// Collapsed-by-default filter bar. Shows active filters as removable tags and
// expands into a floating panel (absolute, so it never pushes the grid down).
function FilterBar({ activeTags = [], onClearAll, children }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <div className="relative flex-1">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-slate-400" aria-hidden>⌕</span>
        {activeTags.length === 0 ? (
          <span className="text-xs text-slate-400">{t("filters.noFilters", "No filters")}</span>
        ) : (
          activeTags.map((tag) => (
            <span
              key={tag.key}
              className="inline-flex items-center gap-1 rounded-full bg-slate-700 px-2.5 py-1 text-xs font-medium text-slate-100"
            >
              {tag.label}
              <button
                type="button"
                onClick={tag.onRemove}
                className="text-slate-400 transition hover:text-white"
                aria-label={`${t("filters.removeTag", "Remove")} ${tag.label}`}
              >
                ✕
              </button>
            </span>
          ))
        )}
        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          className="rounded-full border border-slate-600 bg-slate-700/90 px-3 py-1 text-xs font-semibold text-slate-100 transition hover:bg-slate-600"
        >
          {open ? `${t("filters.button", "Filters")} ▴` : `${t("filters.button", "Filters")} ▾`}
        </button>
        {activeTags.length > 0 && (
          <button
            type="button"
            onClick={onClearAll}
            className="rounded-full px-2 py-1 text-xs font-semibold text-sky-300 transition hover:text-sky-200"
          >
            {t("filters.clear", "Clear")}
          </button>
        )}
      </div>

      {open && (
        <>
          {/* Mobile: full-screen backdrop + bottom sheet so the panel is
              never clipped by viewport height and always scrolls naturally.
              Desktop (sm+): reverts to the original floating dropdown. */}
          <div
            className="fixed inset-0 z-40 bg-slate-950/60 sm:hidden"
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />
          <div
            className="fixed inset-x-0 bottom-0 z-40 flex max-h-[85dvh] flex-col rounded-t-3xl border border-slate-700/70 bg-slate-800/98 shadow-2xl backdrop-blur sm:absolute sm:inset-x-auto sm:bottom-auto sm:left-0 sm:top-full sm:mt-2 sm:max-h-[75vh] sm:w-[min(92vw,720px)] sm:rounded-3xl"
            style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
          >
            <div className="flex-1 overflow-y-auto overscroll-contain p-4">{children}</div>
            <div className="flex justify-end gap-2 border-t border-slate-700/60 p-4 sm:border-t-0 sm:pt-0">
              <button
                type="button"
                onClick={onClearAll}
                className="rounded-xl border border-slate-600 bg-slate-700 px-3 py-2 text-xs font-semibold text-slate-100 transition hover:bg-slate-600"
              >
                {t("filters.clearAll", "Clear all")}
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-2 text-xs font-semibold text-white transition hover:from-blue-500 hover:to-indigo-500"
              >
                {t("common.apply", "Apply")}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default FilterBar;
